import { Box, Stack, Text } from '@ltvco/refresh-lib/theme';
import { IconContainer } from '@ltvco/refresh-lib/v2';
import { type FC, type KeyboardEvent } from 'react';

import {
  RECALL_CARD_FIELDS,
  RECALL_TYPE_ICON,
  type RecallCardFieldRowKey,
} from '../constants';
import { getRecallCardTitle, isHighOrCriticalRecallSeverity } from '../utils';

import {
  RecallCardContainer,
  RecallCardMainContent,
  RecallCardMainStack,
  RecallCardMetricTile,
  RecallCardMetricTilesRow,
  RecallCardWarningBackdrop,
  RecallDescriptionText,
  RecallFieldRow,
  RecallReasonPill,
  RecallTwoLineText,
} from './components';

import type { NormalizedRecallCard } from '../interfaces';

const MAX_VISIBLE_RECALL_PILLS = 2;
const FIELD_ROW_VALUE_WRAP_THRESHOLD = 45;

const METRIC_TILE_LABEL_TO_DETAIL_FIELD: Partial<
  Record<string, RecallCardFieldRowKey>
> = {
  'Risk level': 'severity',
  'Recall date': 'recallDate',
  Type: 'recallSubType',
  Options: 'options',
};

export interface RecallCardProps {
  card: NormalizedRecallCard;
  onClick?: () => void;
}

function handleRecallCardKeyDown(
  e: KeyboardEvent<HTMLDivElement>,
  onClick: () => void
) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onClick();
  }
}

export const RecallCard: FC<RecallCardProps> = ({ card, onClick }) => {
  const Icon = RECALL_TYPE_ICON[card.recallType];
  const title = getRecallCardTitle(card);
  const description = card.description;
  const showWarningBackdrop = isHighOrCriticalRecallSeverity(card.severity);
  const metricTiles = card.metricTiles ?? [];
  const fieldKeysCoveredByMetricTiles = new Set(
    metricTiles
      .map((t) => METRIC_TILE_LABEL_TO_DETAIL_FIELD[t.label])
      .filter((k): k is RecallCardFieldRowKey => k !== undefined)
  );

  const recallPills = card.pills;
  const hasRecallPills = Boolean(recallPills?.length);
  const visibleRecallPills =
    recallPills && recallPills.length > MAX_VISIBLE_RECALL_PILLS
      ? recallPills.slice(0, MAX_VISIBLE_RECALL_PILLS)
      : recallPills ?? [];
  const recallPillsOverflowCount =
    recallPills && recallPills.length > MAX_VISIBLE_RECALL_PILLS
      ? recallPills.length - MAX_VISIBLE_RECALL_PILLS
      : 0;

  const visibleRecallCardFields = RECALL_CARD_FIELDS.filter(
    ({ key }) => card[key] && !fieldKeysCoveredByMetricTiles.has(key)
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
    >
      {showWarningBackdrop ? (
        <RecallCardWarningBackdrop aria-hidden component="div" />
      ) : null}
      <RecallCardContainer
        component="div"
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick ? (e) => handleRecallCardKeyDown(e, onClick) : undefined
        }
        px={1.5}
        py={2}
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'auto',
          ...(onClick
            ? {
                ':hover': {
                  backgroundColor: (theme) => theme.palette.background.default,
                },
              }
            : {}),
        }}
      >
        <RecallCardMainStack spacing={0.5} useFlexGap>
          <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
            <IconContainer
              icon={<Icon color="primary" sx={{ fontSize: 16 }} />}
              sx={{ width: 24, height: 24, flexShrink: 0 }}
            />
            <Text
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontWeight: 400,
              }}
            >
              {card.recallType}
            </Text>
          </Stack>
          <RecallCardMainContent>
            <Stack spacing={1} sx={{ width: '100%', gap: 1 }}>
              <RecallTwoLineText
                variant="subtitle1"
                fontWeight={700}
                sx={{ lineHeight: 1.25 }}
              >
                {title}
              </RecallTwoLineText>
              {description ? (
                <RecallDescriptionText variant="body2" color="text.primary">
                  {description}
                </RecallDescriptionText>
              ) : null}
              {hasRecallPills ? (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                  sx={{ columnGap: 0.75, rowGap: 0.5 }}
                >
                  {visibleRecallPills.map((reasonTile, i) => (
                    <RecallReasonPill
                      key={`${reasonTile}-${i}`}
                      component="span"
                    >
                      {reasonTile}
                    </RecallReasonPill>
                  ))}
                  {recallPillsOverflowCount > 0 ? (
                    <RecallReasonPill
                      key="recall-pills-overflow"
                      component="span"
                      aria-label={`${recallPillsOverflowCount} more reasons`}
                    >
                      +{recallPillsOverflowCount}
                    </RecallReasonPill>
                  ) : null}
                </Stack>
              ) : null}
              {visibleRecallCardFields.length > 0 ? (
                <Stack sx={{ width: '100%', gap: 1 }}>
                  {visibleRecallCardFields.map(({ label, key }) => {
                    const value = card[key];
                    const valueStr = String(value);
                    const wrapValueBelow =
                      valueStr.length > FIELD_ROW_VALUE_WRAP_THRESHOLD;
                    return wrapValueBelow ? (
                      <Stack
                        key={key}
                        direction="column"
                        alignItems="flex-start"
                        gap={0.25}
                        sx={{ width: '100%', minWidth: 0 }}
                      >
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap' }}
                          fontWeight={600}
                        >
                          {label}
                        </Text>
                        <RecallTwoLineText
                          variant="body2"
                          title={valueStr}
                          sx={{ width: '100%', minWidth: 0 }}
                        >
                          {value}
                        </RecallTwoLineText>
                      </Stack>
                    ) : (
                      <RecallFieldRow key={key}>
                        <Text
                          component="span"
                          variant="body2"
                          sx={{ whiteSpace: 'nowrap', mr: 0.5, flexShrink: 0 }}
                          fontWeight={600}
                        >
                          {label}
                        </Text>
                        <RecallTwoLineText
                          variant="body2"
                          title={valueStr}
                          sx={{ minWidth: 0, flex: 1 }}
                        >
                          {value}
                        </RecallTwoLineText>
                      </RecallFieldRow>
                    );
                  })}
                </Stack>
              ) : null}
            </Stack>
          </RecallCardMainContent>
          {metricTiles.length > 0 ? (
            <RecallCardMetricTilesRow pt={2}>
              {metricTiles.map((metricTile) => (
                <RecallCardMetricTile key={metricTile.label}>
                  <Text variant="body2" fontWeight={700} color="text.primary">
                    {metricTile.label}
                  </Text>
                  <Text
                    variant="body2"
                    sx={{
                      color: metricTile.valueEmphasis
                        ? 'error.main'
                        : 'text.primary',
                      wordBreak: 'break-word',
                    }}
                  >
                    {metricTile.value}
                  </Text>
                </RecallCardMetricTile>
              ))}
            </RecallCardMetricTilesRow>
          ) : null}
        </RecallCardMainStack>
      </RecallCardContainer>
    </Box>
  );
};
